// React Query hooks for claims (Stories 2.1, 2.2, 2.3, 2.8)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { syncManager } from '../lib/syncManager';
import { offlineStorage } from '../lib/offlineStorage';
import { useAuthStore } from '../stores/authStore';

// ============================================================
// Type definitions
// ============================================================

export type ClaimType = 'GENERAL' | 'MILEAGE' | 'PETTY_CASH' | 'PROJECT';

export type ClaimStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'ADMIN_APPROVED'
  | 'RETURNED_TO_ADMIN'
  | 'FINANCE_APPROVED'
  | 'APPROVED'
  | 'REJECTED';

export interface Claim {
  id: string;
  userId: string;
  claimMonth: string;
  claimType: ClaimType;
  description?: string | null;
  status: ClaimStatus;
  totalAmount: number;
  referenceNumber: string | null;
  hardcopyReceived?: boolean;
  hardcopyReceivedById?: string | null;
  hardcopyReceivedAt?: string | null;
  rejectionReason?: string | null;
  rejectedById?: string | null;
  rejectedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClaimInput {
  claimMonth: string;
  claimType?: ClaimType;
  description?: string;
}

export interface UpdateClaimInput {
  claimType?: ClaimType;
  description?: string | null;
}

export interface MileageEntry {
  id: string;
  claimId: string;
  date: string;
  origin: string;
  destination: string;
  purpose: string | null;
  vehicleType: string | null;
  originType: string;
  destType: string;
  distance: number;
  distanceOverride: number | null;
  overrideReason: string | null;
  homeOfficeDeduction: number;
  claimableDistance: number;
  rateUsed: number | null;
  amount: number;
  isRoundTrip: boolean;
  userLocationId?: string | null;
  userLocation?: { id: string; label: string; address: string; locationType: string } | null;
  parkingEntryId?: string | null;
  tollEntryId?: string | null;
  parkingEntry?: { id: string; description: string; amount: number; date: string } | null;
  tollEntry?: { id: string; description: string; amount: number; date: string } | null;
  adminVerified?: boolean;
  adminVerifiedById?: string | null;
  adminVerifiedAt?: string | null;
  financeVerified?: boolean;
  financeVerifiedById?: string | null;
  financeVerifiedAt?: string | null;
  hrVerified?: boolean;
  hrVerifiedById?: string | null;
  hrVerifiedAt?: string | null;
  flagReason?: string | null;
  flaggedById?: string | null;
  flaggedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  type: 'mileage';
}

export interface Category {
  id: string;
  name: string;
  sortOrder: number;
}

export interface Receipt {
  id: string;
  filename: string;
  originalFilename?: string;
  mimeType?: string;
  size: number;
}

export interface PettyCashEntry {
  id: string;
  claimId: string;
  date: string;
  description: string;
  amount: number;
  categoryId: string;
  category?: { id: string; name: string };
  receipts?: Receipt[];
  adminVerified?: boolean;
  adminVerifiedById?: string | null;
  adminVerifiedAt?: string | null;
  financeVerified?: boolean;
  financeVerifiedById?: string | null;
  financeVerifiedAt?: string | null;
  hrVerified?: boolean;
  hrVerifiedById?: string | null;
  hrVerifiedAt?: string | null;
  flagReason?: string | null;
  flaggedById?: string | null;
  flaggedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  type: 'petty-cash';
}

export interface FlaggedBy {
  id: string;
  name: string;
  role: string;
}

export type ExpenseItem =
  | (MileageEntry & { type: 'mileage'; flaggedBy?: FlaggedBy | null })
  | (PettyCashEntry & { type: 'petty-cash'; flaggedBy?: FlaggedBy | null });

const FLAGGER_ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  IT_ADMIN: 'Admin',
  FINANCE: 'Finance',
  FINANCE_LEAD: 'Finance',
  HR: 'HR',
};

/** "Finance (Jane Tan)" — falls back to "Reviewer" when the flagger is unknown. */
export function describeFlagger(flaggedBy?: FlaggedBy | null) {
  if (!flaggedBy) return 'Reviewer';
  const role = FLAGGER_ROLE_LABEL[flaggedBy.role] ?? 'Reviewer';
  return `${role} (${flaggedBy.name})`;
}

export interface ExpenseSummary {
  totalAmount: number;
  mileageCount: number;
  pettyCashCount: number;
  mileageTotal: number;
  pettyCashTotal: number;
}

// ============================================================
// Query keys
// ============================================================

export const claimsKeys = {
  all: ['claims'] as const,
  list: (filters?: { status?: string }) => [...claimsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...claimsKeys.all, 'detail', id] as const,
  mileage: (claimId: string) => [...claimsKeys.all, claimId, 'mileage'] as const,
  pettyCash: (claimId: string) => [...claimsKeys.all, claimId, 'petty-cash'] as const,
  expenses: (claimId: string) => [...claimsKeys.all, claimId, 'expenses'] as const,
  categories: () => ['categories'] as const,
  mileageRates: () => ['mileage-rates'] as const,
};

// ============================================================
// Claim hooks
// ============================================================

export function useClaims(filters?: { status?: string; type?: string }) {
  return useQuery<{ claims: Claim[]; meta: { pagination: { page: number; total: number; totalPages: number } } }>({
    queryKey: claimsKeys.list(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.type) params.append('type', filters.type);
      const qs = params.toString() ? `?${params.toString()}` : '';
      return api.get(`/claims${qs}`);
    },
  });
}

export function useClaim(id: string) {
  return useQuery<{ claim: Claim }>({
    queryKey: claimsKeys.detail(id),
    queryFn: () => api.get(`/claims/${id}`),
    enabled: !!id,
  });
}

export function useCreateClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim; existing: boolean; message?: string }, Error, CreateClaimInput>({
    mutationFn: (data) => api.post('/claims', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.list() });
    },
  });
}

export function useUpdateClaim(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ claim: Claim }, Error, UpdateClaimInput>({
    mutationFn: (data) => api.patch(`/claims/${claimId}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.list() });
    },
  });
}

export interface ClaimStatusHistory {
  id: string;
  claimId: string;
  fromStatus: string;
  toStatus: string;
  userId: string;
  userName?: string | null;
  note?: string | null;
  createdAt: string;
}

export function useClaimTimeline(claimId: string) {
  return useQuery<{ timeline: ClaimStatusHistory[] }>({
    queryKey: [...claimsKeys.detail(claimId), 'timeline'],
    queryFn: () => api.get(`/claims/${claimId}/timeline`),
    enabled: !!claimId,
  });
}

// ============================================================
// Mileage hooks (Story 2.2, 2.6, 2.7)
// ============================================================

export function useMileageEntries(claimId: string) {
  return useQuery<{ entries: MileageEntry[] }>({
    queryKey: claimsKeys.mileage(claimId),
    queryFn: () => api.get(`/claims/${claimId}/mileage`),
    enabled: !!claimId,
  });
}

export interface CreateMileageInput {
  date: string;
  origin: string;
  destination: string;
  purpose?: string;
  vehicleType?: string;
  originType?: 'HOME' | 'OFFICE' | 'OTHER';
  destType?: 'HOME' | 'OFFICE' | 'OTHER';
  isRoundTrip?: boolean;
  distanceOverride?: number;
  overrideReason?: string;
  parkingEntryId?: string | null;
  tollEntryId?: string | null;
  userLocationId?: string | null;
}

export function useCreateMileage(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ entry: MileageEntry }, Error, CreateMileageInput>({
    mutationFn: async (data) => {
      if (syncManager.isOnline()) {
        return api.post(`/claims/${claimId}/mileage`, data);
      } else {
        const userId = useAuthStore.getState().user?.id;
        if (!userId) throw new Error('You must be signed in to save a draft offline');
        const localId = await offlineStorage.saveDraft(userId, claimId, 'mileage', data as unknown as Record<string, unknown>);
        // Return a synthetic response for UI optimism
        return {
          entry: {
            id: localId,
            claimId,
            date: data.date,
            origin: data.origin,
            destination: data.destination,
            purpose: data.purpose ?? null,
            vehicleType: data.vehicleType ?? null,
            originType: data.originType ?? 'OTHER',
            destType: data.destType ?? 'OTHER',
            distance: 0,
            distanceOverride: data.distanceOverride ?? null,
            overrideReason: data.overrideReason ?? null,
            homeOfficeDeduction: 0,
            claimableDistance: 0,
            rateUsed: null,
            amount: 0,
            isRoundTrip: data.isRoundTrip ?? false,
            userLocationId: data.userLocationId ?? null,
            parkingEntryId: data.parkingEntryId ?? null,
            tollEntryId: data.tollEntryId ?? null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            type: 'mileage' as const,
          },
        };
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.mileage(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
    },
  });
}


export function useUpdateMileage(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ entry: MileageEntry }, Error, { entryId: string; data: Partial<CreateMileageInput> }>({
    mutationFn: ({ entryId, data }) => api.put(`/claims/${claimId}/mileage/${entryId}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.mileage(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
    },
  });
}

export function useDeleteMileage(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (entryId) => api.delete(`/claims/${claimId}/mileage/${entryId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.mileage(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
    },
  });
}

// ============================================================
// Petty Cash hooks (Story 2.3, 2.6, 2.7)
// ============================================================

export function usePettyCashEntries(claimId: string) {
  return useQuery<{ entries: PettyCashEntry[] }>({
    queryKey: claimsKeys.pettyCash(claimId),
    queryFn: () => api.get(`/claims/${claimId}/petty-cash`),
    enabled: !!claimId,
  });
}

export interface CreatePettyCashInput {
  date: string;
  description: string;
  amount: number;
  categoryId: string;
}

export function useCreatePettyCash(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ entry: PettyCashEntry }, Error, CreatePettyCashInput>({
    mutationFn: async (data) => {
      if (syncManager.isOnline()) {
        return api.post(`/claims/${claimId}/petty-cash`, data);
      } else {
        const userId = useAuthStore.getState().user?.id;
        if (!userId) throw new Error('You must be signed in to save a draft offline');
        const localId = await offlineStorage.saveDraft(userId, claimId, 'petty-cash', data as unknown as Record<string, unknown>);
        return {
          entry: {
            id: localId,
            claimId,
            date: data.date,
            description: data.description,
            amount: data.amount,
            categoryId: data.categoryId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            type: 'petty-cash',
          },
        };
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.pettyCash(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
    },
  });
}

export function useUpdatePettyCash(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ entry: PettyCashEntry }, Error, { entryId: string; data: Partial<CreatePettyCashInput> }>({
    mutationFn: ({ entryId, data }) => api.put(`/claims/${claimId}/petty-cash/${entryId}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.pettyCash(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
    },
  });
}

export function useDeletePettyCash(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (entryId) => api.delete(`/claims/${claimId}/petty-cash/${entryId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.pettyCash(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
    },
  });
}

// Delete an entire DRAFT claim (owner only). Cascades entries + receipts on the server.
export function useDeleteClaim() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (claimId) => api.delete(`/claims/${claimId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.list() });
    },
  });
}

// ============================================================
// Categories (Story 2.3)
// ============================================================

export function useCategories() {
  return useQuery<{ categories: Category[] }>({
    queryKey: claimsKeys.categories(),
    queryFn: () => api.get('/config/categories'),
    staleTime: 10 * 60 * 1000, // 10 min
  });
}

// Story 3.6: Vehicle rates + office address
export interface MileageRates {
  rates: Record<string, number>;
  officeAddress: string;
}

export function useMileageRates() {
  return useQuery<MileageRates>({
    queryKey: claimsKeys.mileageRates(),
    queryFn: () => api.get('/config/mileage-rates'),
    staleTime: 5 * 60 * 1000,
  });
}

// Story 3.1/3.2/3.5: Live calculation preview
export interface MileageBreakdownPreview {
  breakdown: {
    totalDistanceKm: number;
    homeOfficeDeductionKm: number;
    claimableDistanceKm: number;
    ratePerKm: number;
    amountMyr: number;
    deductionApplied: boolean;
    deductionExplanation: string;
  };
  ratePerKm: number;
  vehicleType: string;
  mapsAvailable: boolean;
  resolvedOrigin: string;
  resolvedDestination: string;
}

export function useMileageCalculate() {
  return useMutation<MileageBreakdownPreview, Error, {
    origin: string;
    destination: string;
    isRoundTrip: boolean;
    vehicleType: string;
    originType: string;
    destType: string;
    distanceOverride?: number;
    userLocationId?: string;
  }>({
    mutationFn: (data) => api.post('/mileage/calculate', data),
  });
}

export function usePlaceAutocomplete() {
  return useMutation<{ predictions: string[] }, Error, string>({
    mutationFn: (input) => api.get(`/mileage/autocomplete?input=${encodeURIComponent(input)}`),
  });
}

// ============================================================
// Unified Expense List (Story 2.8)
// ============================================================

export function useExpenses(claimId: string) {
  const userId = useAuthStore((state) => state.user?.id);
  return useQuery<{ expenses: ExpenseItem[]; summary: ExpenseSummary; claim: Claim }>({
    queryKey: claimsKeys.expenses(claimId),
    queryFn: async () => {
      const result = await api.get<{ expenses: ExpenseItem[]; summary: ExpenseSummary; claim: Claim }>(
        `/claims/${claimId}/expenses`,
      );
      if (!userId) return result;
      const localDrafts = await offlineStorage.getDraftsForClaim(userId, claimId);
      const pending = localDrafts.filter((draft) => draft.syncStatus !== 'synced' && !draft.deleted);
      const localExpenses = pending.map((draft) => ({
        ...(draft.data as object),
        id: draft.serverId ?? draft.localId,
        claimId: draft.claimId,
        type: draft.type,
        amount: Number((draft.data as { amount?: number }).amount ?? 0),
        date: (draft.data as { date?: string }).date ?? new Date().toISOString(),
      })) as ExpenseItem[];
      return {
        ...result,
        expenses: [...localExpenses, ...result.expenses],
      };
    },
    enabled: !!claimId,
    // Reviewers further up the chain can flag/verify entries at any time;
    // always refetch on open so a returned claim never shows stale verification.
    refetchOnMount: 'always',
  });
}

// ============================================================
// Receipt hooks (Story 2.4)
// ============================================================

export function useDeleteReceipt(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (receiptId) => api.delete(`/claims/receipts/${receiptId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.pettyCash(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
    },
  });
}

// ============================================================
// Story 4.1 + 4.5 — Submit Claim
// ============================================================

export interface SubmitClaimSuccess {
  claim: Claim;
}

/**
 * Submit a DRAFT claim.
 * On error, the thrown ApiError carries:
 *   - status 422: code VALIDATION_FAILED, payload.errors = ValidationError[]
 *   - status 409: code PENDING_SYNC when offline drafts have not drained
 *   - status 400: code NOT_DRAFT
 */
export function useSubmitClaim(claimId: string) {
  const queryClient = useQueryClient();
  return useMutation<SubmitClaimSuccess, import('../lib/api').ApiError>({
    mutationFn: async () => {
      await syncManager.processQueue();
      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        const pending = (await offlineStorage.getPendingEntries(userId)).filter(
          (entry) => entry.claimId === claimId && !entry.deleted,
        );
        if (pending.length > 0) {
          throw new ApiError(409, 'PENDING_SYNC', 'Offline expenses are still syncing. Wait until they appear, then submit.');
        }
      }
      return api.post(`/claims/${claimId}/submit`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: claimsKeys.detail(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.list() });
    },
  });
}

// ============================================================
// System Config Hook (Issue #18 / CAP-7)
// ============================================================

export interface SystemConfig {
  mileageRateMyr: number;
  vehicleRateCar: number;
  vehicleRateMotorcycle: number;
  vehicleRateOther: number;
  officeAddress: string;
  maxClaimAmountMyr: number;
  submissionDeadlineDay: number;
  approvalReminderDays: number;
  allowedFileTypes: string[];
  maxFileSizeMb: number;
  hardcopyRemark?: string;
  documentClassificationDefault?: string;
}

export function useSystemConfig() {
  return useQuery<{ config: SystemConfig }>({
    queryKey: ['config'],
    queryFn: () => api.get('/config'),
    staleTime: 5 * 60 * 1000,
  });
}



